import React, { useRef, useState } from 'react';
import { IonButton, IonButtons, IonHeader, IonIcon, IonPopover, IonTitle, IonToolbar } from '@ionic/react';
import { useHistory } from 'react-router';
import { chevronBack } from 'ionicons/icons';
import './MobileDetailPageHeader.css';
import { AppLimitedMode, useLimitedMode } from '../../../utils/auth/useLimitedMode';
import { uniqueId } from 'lodash';
import { warning } from 'ionicons/icons';
import { FormattedMessage } from 'react-intl';
import { GenericMessages } from '../../../intl/GenericMessages';
import { DqText } from '../../dq-text';
import { useHasTouchscreen } from '../../../utils/helpers/platformHelpers';

interface MobileHeaderProps {
  title?: string;
  /** The right side of the header. Can be any react element. */
  right?: React.ReactNode;
  /** Optional. Allows parent of header component to set a static location for the back button to redirect to. */
  backButtonUrl?: string;
}

const MobileDetailPageHeader: React.FC<MobileHeaderProps> = ({ title, right, backButtonUrl }) => {
  const history = useHistory();
  const limitedMode = useLimitedMode();
  const [limitedModeId] = useState(uniqueId('limited-mode-'));
  const limitedModePopover = useRef<HTMLIonPopoverElement>(null);
  const hasTouchscreen = useHasTouchscreen();

  const goBack = React.useCallback(() => {
    if (backButtonUrl) {
      history.push(backButtonUrl);
      return;
    }

    history.goBack();
  }, [backButtonUrl, history]);

  const buttons = React.useMemo(() => {
    const buttonList = !!right ? [right] : [];
    if (limitedMode.isLimited) {
      buttonList.unshift(
        <IonButton fill="clear" id={limitedModeId} color="danger">
          <IonIcon slot="icon-only" icon={warning} />
        </IonButton>,
      );
    }

    return buttonList;
  }, [limitedMode.isLimited, limitedModeId, right]);

  return (
    <IonHeader>
      <IonToolbar>
        <IonButtons slot="start">
          <IonButton onClick={() => goBack()}>
            <IonIcon size="large" color="dark" slot="icon-only" icon={chevronBack} />
          </IonButton>
        </IonButtons>
        <IonTitle>{title}</IonTitle>

        {buttons.length > 0 && <IonButtons slot="end">{React.Children.map(buttons, i => i)}</IonButtons>}
        {limitedMode.isLimited && (
          <IonPopover
            trigger={limitedModeId}
            ref={limitedModePopover}
            triggerAction={hasTouchscreen ? 'click' : 'hover'}
          >
            <div className="ion-padding">
              <DqText>
                {limitedMode.mode === AppLimitedMode.Offline ? (
                  <FormattedMessage {...GenericMessages.headerBannerLimitedOffline} />
                ) : limitedMode.mode === AppLimitedMode.SessionExpired ? (
                  <FormattedMessage {...GenericMessages.headerBannerLimitedSessionExpired} />
                ) : (
                  <FormattedMessage {...GenericMessages.headerBannerLimitedUnknown} />
                )}
              </DqText>
            </div>
          </IonPopover>
        )}
      </IonToolbar>
    </IonHeader>
  );
};

export default MobileDetailPageHeader;
